import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Image } from '../../Image';
import { trackBoxToggle } from '@/lib/reportBoxToggle';
import { toSpecialNameSlug } from '@/lib/reportAnalyticsSections';
import { getFilledBrands } from '../utils/hasContent';
import { useReportI18n } from '@/pages/reports/i18n';

export interface BrandEntry {
  imageUrl: string;
  name: string;
  username: string;
  views: string;
  likes: string;
  link?: string;
}

export interface CollapsibleBrandsProps {
  title?: string;
  subtitle?: string;
  brands?: BrandEntry[];
}

export const CollapsibleBrands: React.FC<CollapsibleBrandsProps> = ({
  title,
  subtitle,
  brands = [],
}) => {
  const { t } = useReportI18n();
  const [isOpen, setIsOpen] = useState(true);
  const heading = title || t('tiktokTrends.brands');
  const description = subtitle || t('tiktokTrends.brandsSubtitle');
  const boxSectionName = toSpecialNameSlug(heading);
  const filledBrands = getFilledBrands(brands);

  if (filledBrands.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex justify-center">
      <div
        className="w-full bg-white overflow-hidden"
        data-report-box
        data-report-box-section-name={boxSectionName}
      >
        <button
          type="button"
          onClick={(event) => {
            const next = !isOpen;
            setIsOpen(next);
            trackBoxToggle(next, event.currentTarget.parentElement);
          }}
          className="w-full flex items-center gap-2 bg-black text-white px-4 py-3 text-left hover:bg-gray-900 transition-colors font-['Times_New_Roman']"
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5 shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 shrink-0" />
          )}
          <span className="text-[36px] leading-[40px] tracking-tight">{heading}</span>
        </button>
        {isOpen && (
          <div className="p-4 pt-2 space-y-6 font-['Times_New_Roman']">
            {description && (
              <p className="text-sm text-gray-600">{description}</p>
            )}
            <div className="space-y-8">
              {filledBrands.map((brand, idx) => {
                  const content = (
                    <>
                      <div className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                        <Image
                          src={brand.imageUrl}
                          alt={brand.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="font-['Times_New_Roman'] font-bold text-gray-900 text-xl leading-none underline">
                          {brand.name}
                        </p>
                        <p className="font-['Times_New_Roman'] font-normal text-gray-700 text-base">
                          @{brand.username.replace(/^@/, '')}
                        </p>
                        <p className="font-['Times_New_Roman'] italic text-gray-600 text-sm mt-1">
                          {brand.views}
                        </p>
                        {brand.likes && (
                          <p className="font-['Times_New_Roman'] italic text-gray-600 text-sm">
                            {brand.likes}
                          </p>
                        )}
                      </div>
                    </>
                  );
                  return (
                    <div key={idx} className="flex gap-4 items-start">
                      {brand.link ? (
                        <a
                          href={brand.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex gap-4 items-start hover:opacity-90"
                        >
                          {content}
                        </a>
                      ) : (
                        content
                      )}
                    </div>
                  );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
